import { useMemo } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded'
import { useAuth } from '../context/useAuth'
import { Button } from './Common/Button'
import { toConsolePath } from '../utils/consoleRoutes'
import './notFoundPage.css'

type NotFoundPageProps = {
  path?: string
}

const truncatePath = (value: string, max = 96): string =>
  value.length <= max ? value : `${value.slice(0, max - 1)}…`

/**
 * Universal "URL doesn't exist" surface. Used by main.tsx for any pathname
 * that doesn't match a recognized top-level route. Deliberately says nothing
 * about internal wrapper roles, APK builds, or app-only surfaces — those are
 * product internals and never surface to random URL guessers.
 */
export const NotFoundPage = ({ path }: NotFoundPageProps) => {
  const { currentUser } = useAuth()
  const reducedMotion = useReducedMotion()

  const displayPath = useMemo(() => {
    const raw = path
      || (typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '')
    return truncatePath(raw || '/')
  }, [path])

  const homeHref = currentUser ? toConsolePath('/') : '/'
  const goHome = () => {
    if (typeof window === 'undefined') return
    if (window.location.pathname === homeHref) {
      window.dispatchEvent(new PopStateEvent('popstate'))
      return
    }
    window.history.pushState(null, '', homeHref)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  return (
    <main className="notFoundStage" aria-labelledby="notFoundHeading" data-testid="not-found-page">
      <div className="notFoundBackdrop" aria-hidden="true">
        <svg viewBox="0 0 800 800" role="presentation" focusable="false">
          <defs>
            <radialGradient id="notFoundGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.25" />
              <stop offset="60%" stopColor="currentColor" stopOpacity="0.08" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx="400" cy="400" r="380" fill="url(#notFoundGlow)" />
          <motion.g
            initial={reducedMotion ? undefined : { rotate: 0 }}
            animate={reducedMotion ? undefined : { rotate: 360 }}
            transition={reducedMotion ? undefined : { duration: 90, repeat: Infinity, ease: 'linear' }}
            style={{ transformOrigin: '400px 400px' }}
            stroke="currentColor"
            strokeOpacity="0.35"
            fill="none"
          >
            <ellipse cx="400" cy="400" rx="340" ry="140" />
            <ellipse cx="400" cy="400" rx="260" ry="220" transform="rotate(28 400 400)" />
            <ellipse cx="400" cy="400" rx="180" ry="300" transform="rotate(64 400 400)" />
          </motion.g>
          <motion.circle
            cx="400"
            cy="400"
            r="4"
            fill="currentColor"
            initial={{ opacity: 0.55 }}
            animate={reducedMotion ? undefined : { opacity: [0.55, 1, 0.55] }}
            transition={reducedMotion ? undefined : { duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
          />
        </svg>
      </div>

      <motion.section
        className="notFoundPanel"
        initial={reducedMotion ? { opacity: 1 } : { opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 130, damping: 24 }}
      >
        <span className="notFoundEyebrow">Not found</span>
        <h1 id="notFoundHeading" className="notFoundHeading">This URL doesn&rsquo;t exist.</h1>
        <p className="notFoundBody">
          The link may be mistyped, expired, or from a version of the app that has moved on. Nothing
          is broken on your end.
        </p>
        <code className="notFoundPath" data-testid="not-found-path">{displayPath}</code>
        <div className="notFoundActions">
          <Button
            variant="primary"
            size="lg"
            onClick={goHome}
            data-testid="not-found-home"
            icon={<HomeRoundedIcon fontSize="small" />}
          >
            Take me home
          </Button>
          <a
            className="notFoundQuiet"
            href="/docs"
            data-testid="not-found-guidebook"
          >
            <MenuBookRoundedIcon fontSize="small" />
            Open the guidebook
            <ArrowOutwardRoundedIcon fontSize="small" />
          </a>
        </div>
      </motion.section>
    </main>
  )
}
